import { useCallback } from 'react';
import { useGameStore, useUIStore, useNetworkStore, useSettingsStore } from '@/stores';
import './GameControls.css';

// 注意: このコンポーネントはオンラインモードでsendDrawCard()を使用

/**
 * GameControls - ゲームアクションボタン
 * カードドローのみを提供（UNO宣言ボタンはプロトタイプ仕様で廃止）
 */
const GameControls = () => {
  const currentPlayer = useGameStore((state) => state.getCurrentPlayer());
  const mode = useGameStore((state) => state.mode);
  const localPlayerId = useGameStore((state) => state.localPlayerId);
  const gameState = useGameStore((state) => state.gameState);
  const drawCard = useGameStore((state) => state.drawCard);
  const passTurn = useGameStore((state) => state.passTurn);
  const isTransitionBlocking = useUIStore((state) => state.isTransitionBlocking());

  // オンラインモード用のネットワーク送信関数
  const sendDrawCard = useNetworkStore((state) => state.sendDrawCard);

  // 4-icon: SETTINGS / SOUND / MENU 用
  const openSettings = useUIStore((state) => state.openSettings);
  const openRules = useUIStore((state) => state.openRules);
  const navigateTo = useUIStore((state) => state.navigateTo);
  const showToast = useUIStore((state) => state.showToast);
  const audio = useSettingsStore((state) => state.audio);
  const updateAudio = useSettingsStore((state) => state.updateAudio);
  const resetGame = useGameStore((state) => state.resetGame);

  // 現在のプレイヤーかどうか
  const isCurrentPlayer = currentPlayer?.id === localPlayerId;

  // デッキの残り枚数
  const deckCount = gameState?.deck.length || 0;

  // 既にこのターンでドローしたか（パスボタン表示用）
  // オンラインモードでは現状サポートしない（サーバー側未対応）
  const hasDrawnThisTurn = mode !== 'online' && !!gameState?.hasDrawnThisTurn;
  const showPassButton = isCurrentPlayer && hasDrawnThisTurn;

  /**
   * カードを引くハンドラ
   * オンラインモードではサーバーに送信、それ以外はローカルで処理
   */
  const handleDrawCard = useCallback(async () => {
    if (!localPlayerId || !isCurrentPlayer) return;

    try {
      if (mode === 'online') {
        // オンラインモード: サーバーに送信
        sendDrawCard();
      } else {
        // ソロ/ローカルモード: ローカルで処理
        await drawCard(localPlayerId);
      }
    } catch (error) {
      console.error('Failed to draw card:', error);
    }
  }, [localPlayerId, isCurrentPlayer, mode, sendDrawCard, drawCard]);

  /**
   * ターンをパスするハンドラ（任意ドロー後のみ有効）
   */
  const handlePassTurn = useCallback(async () => {
    if (!localPlayerId || !isCurrentPlayer) return;

    try {
      await passTurn(localPlayerId);
    } catch (error) {
      console.error('Failed to pass turn:', error);
    }
  }, [localPlayerId, isCurrentPlayer, passTurn]);

  /**
   * RULES: 遊び方モーダルを開く
   */
  const handleRulesClick = useCallback(() => {
    openRules();
  }, [openRules]);

  /**
   * SETTINGS: 設定モーダルを開く
   */
  const handleSettingsClick = useCallback(() => {
    openSettings();
  }, [openSettings]);

  /**
   * SOUND: 効果音とBGMをまとめてオン/オフ
   */
  const handleSoundClick = useCallback(() => {
    const nextEnabled = !(audio.soundEnabled || audio.musicEnabled);
    updateAudio({
      soundEnabled: nextEnabled,
      musicEnabled: nextEnabled,
    });
    showToast(
      `サウンドを ${nextEnabled ? 'ON' : 'OFF'} にしました`,
      'info',
      1500
    );
  }, [audio.soundEnabled, audio.musicEnabled, updateAudio, showToast]);

  /**
   * MENU: メインメニューへ戻る（ゲームをリセット）
   */
  const handleMenuClick = useCallback(() => {
    const confirmExit = window.confirm(
      'メインメニューに戻ります。現在のゲームは終了します。よろしいですか？'
    );
    if (!confirmExit) return;
    resetGame();
    navigateTo('main_menu');
  }, [navigateTo, resetGame]);

  if (!gameState) {
    return null;
  }

  // 任意ドロー後はドローボタンを無効化（ドローは1ターンに1回まで）
  const drawDisabled = !isCurrentPlayer || deckCount === 0 || isTransitionBlocking || hasDrawnThisTurn;

  return (
    <div className="GameControls">
      <div className="GameControls__actions">
        {/* YOUR TURN インジケータ — 自分のターンの時のみ表示 */}
        {isCurrentPlayer && (
          <img
            src="/assets/redesign-v3/hud/your-turn.png"
            alt="YOUR TURN"
            className="GameControls__your-turn"
            draggable={false}
          />
        )}

        {/* DRAW CARD ボタン — PNG ベース。クリック領域は button 要素 */}
        <button
          className={`GameControls__draw-btn ${
            drawDisabled ? 'GameControls__draw-btn--disabled' : ''
          }`}
          onClick={handleDrawCard}
          disabled={drawDisabled}
          aria-label="カードを引く"
        >
          <img
            src="/assets/redesign-v3/hud/button-draw-card.png"
            alt=""
            aria-hidden="true"
            className="GameControls__draw-btn-img"
            draggable={false}
          />
        </button>

        {showPassButton && (
          <button
            className={`GameControls__pass-btn ${
              isTransitionBlocking ? 'GameControls__pass-btn--disabled' : ''
            }`}
            onClick={handlePassTurn}
            disabled={isTransitionBlocking}
            aria-label="ターンをパス"
          >
            <span className="GameControls__pass-btn-icon">⏭</span>
            <span className="GameControls__pass-btn-label">PASS</span>
          </button>
        )}

        {/* RULES / SETTINGS / SOUND / MENU 4-アイコン
            装飾 PNG (icon-set.png) の上に透明な 4 ボタンを絶対配置で重ねる */}
        <div
          className="GameControls__icon-set-wrap"
          aria-label="ゲーム HUD コントロール"
          role="group"
        >
          <img
            src="/assets/redesign-v3/hud/icon-set.png"
            alt=""
            aria-hidden="true"
            className="GameControls__icon-set"
            draggable={false}
          />
          <button
            type="button"
            className="GameControls__icon-btn GameControls__icon-btn--1"
            onClick={handleRulesClick}
            aria-label="ルール"
            title="ルール"
          >
            <span className="GameControls__sr-only">ルール</span>
          </button>
          <button
            type="button"
            className="GameControls__icon-btn GameControls__icon-btn--2"
            onClick={handleSettingsClick}
            aria-label="設定を開く"
            title="設定"
          >
            <span className="GameControls__sr-only">設定</span>
          </button>
          <button
            type="button"
            className="GameControls__icon-btn GameControls__icon-btn--3"
            onClick={handleSoundClick}
            aria-label={`サウンドを${audio.soundEnabled || audio.musicEnabled ? 'OFF' : 'ON'}にする`}
            title="サウンド切替"
            aria-pressed={audio.soundEnabled || audio.musicEnabled}
          >
            <span className="GameControls__sr-only">サウンド</span>
          </button>
          <button
            type="button"
            className="GameControls__icon-btn GameControls__icon-btn--4"
            onClick={handleMenuClick}
            aria-label="メインメニューに戻る"
            title="メインメニュー"
          >
            <span className="GameControls__sr-only">メニュー</span>
          </button>
        </div>

        {/* デッキ残り表示 (右端の小バッジ) */}
        <div className="GameControls__deck-info" aria-label={`デッキ残り ${deckCount}枚`}>
          <span className="GameControls__deck-label">DECK</span>
          <span className="GameControls__deck-value">{deckCount}</span>
        </div>
      </div>
    </div>
  );
};

export default GameControls;
